import { Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { loginRequiredGuard } from '@processpuzzle/auth/feature';
import { PLATFORM_SUBSCRIPTION_TRANSLOCO_SCOPE } from './platform-subscription.i18n';
import { CustomerHomeComponent } from './feature/customer-home.component';
import { SignupConfirmationComponent } from './feature/signup-confirmation.component';
import { SubscriptionFunnelComponent } from './feature/subscription-funnel.component';

/**
 * The sign-up funnel, and the confirmation page that follows it.
 *
 * **Two routes, not one, and the second is a child on purpose.**
 *
 * A child route, because `navigationItems` reads only top-level `router.config` — so the
 * confirmation page never appears in the sidenav beside the funnel that leads to it. Nesting is what
 * keeps one sidenav entry for one surface.
 *
 * Its own route rather than a sixth stepper step, because it receives the submitted key and uses it
 * to display the deterministic application address immediately. A sixth step would return a reload
 * to an empty form, having lost that key.
 *
 * **Neither route has an authentication resolver.** The funnel is public deliberately: a visitor
 * comparing prices must reach the plans without being bounced to Keycloak, and the contract requires
 * an account only to *submit*. `SubscriptionFunnelComponent.submit` is where `authenticate()` runs.
 * The confirmation page is public for the same reason its endpoint is — a tenant that is still
 * provisioning has no member for a membership check to recognise.
 */
export const SUBSCRIPTION_ROUTES: Routes = [
  {
    path: '',
    component: SubscriptionFunnelComponent,
    providers: [subscriptionScope()],
  },
  {
    path: 'status/:orgKey',
    component: SignupConfirmationComponent,
    providers: [subscriptionScope()],
  },
];

/**
 * The customer portal.
 *
 * One route, and no `:orgKey` in it. The key is not a path parameter because it is not what grants
 * access — the token's `organization` claim is — and a URL carrying it would read as a link a
 * customer could share. It also keeps one activation redirect valid for every tenant. The key comes
 * from `RememberedOrgKeyStorage` or from the prompt instead.
 *
 * The whole surface answers 404 when `platform-subscription.portal.enabled` is unset, because the
 * controller behind it is not registered — which the portal renders as its key prompt, since a
 * disabled portal and an unknown organization are indistinguishable by design.
 *
 * Unlike the public subscription funnel, the portal is only for the organization administrator,
 * who logs in with the account the activation mail set a password for — the portal API refuses any
 * organization the token's `organization` claim does not name. A route guard, rather than an
 * authentication resolver, is required here: a resolver can finish with no session and still
 * activate the component.
 *
 * `loginRequiredGuard` rather than `authGuard`: the activation mail lands here on a cold page load,
 * and `authGuard`'s detour through `/auth/login` would return the customer to `/` after logging in.
 */
export const CUSTOMER_PORTAL_ROUTES: Routes = [
  {
    path: '',
    component: CustomerHomeComponent,
    canActivate: [loginRequiredGuard],
    providers: [subscriptionScope()],
  },
];

/**
 * This library's transloco scope, with its alias spelled out.
 *
 * The alias is not optional: transloco camel-cases the default one, which would turn
 * `platform_subscription` into `platformSubscription` and silently miss every key below it.
 *
 * Only this scope, unlike platform-admin's routes, which register `base_entity` alongside their own.
 * Nothing here renders base-entity's generic tabs or toolbar — these are hand-written forms rather
 * than generated screens — so there are no `base_entity.*` keys to resolve.
 */
function subscriptionScope() {
  return provideTranslocoScope({ scope: PLATFORM_SUBSCRIPTION_TRANSLOCO_SCOPE, alias: PLATFORM_SUBSCRIPTION_TRANSLOCO_SCOPE });
}
